/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React from 'react';
import { useSelector } from 'react-redux';

import { AttributeMapping } from '../../../../components/AttributeMapping';
import type { RootState } from '../../../../services/serviceTypes';
import { useFetchAttributesDataQuery } from '../../data-store/copilotApi';
import useRemoveAccessLogic from '../../hooks/removeAccessLogic';
import ErrorBanner from '../ErrorBanner/ErrorBanner';

interface RemoveAccessAttributeMappingExpandedViewProps {
  entitlementIndex: number;
}

/**
 * RemoveAccessAttributeMappingExpandedView - For RemoveAccess attribute mappings in expanded view
 */
function RemoveAccessAttributeMappingExpandedView({
  entitlementIndex,
}: RemoveAccessAttributeMappingExpandedViewProps) {
  // Get actual step data from Redux
  const { currentStepData, stepFormData } = useSelector(
    (state: RootState) => state.copilot
  );

  const {
    allValidationErrors,
    handleMappingsChange,
    createDynamicConfig,
    getInitialMappings,
  } = useRemoveAccessLogic();

  // Get existing mappings for this entitlement index
  const currentMappings = getInitialMappings(entitlementIndex);
  const validationError = allValidationErrors[entitlementIndex] || null;

  // Get step data from Redux - try multiple sources
  const stepData = currentStepData.fields[0] || {
    apiURL: '',
    domainObjects: [],
    attributeDefaultValue: '',
  };

  // Fallback to get API data from Connection step if not available in current step
  const connectionData = stepFormData.Connection || {};
  const apiURL = String(
    (stepData as { apiURL?: string }).apiURL ||
      connectionData['connection.url'] ||
      ''
  );
  const domainObjects = (stepData as { domainObjects?: string[] })
    .domainObjects || ['users', 'accounts', 'entitlements'];

  const queryResult = useFetchAttributesDataQuery(
    {
      apiURL,
      domainObjects,
    },
    {
      skip: !apiURL || !domainObjects.length,
    }
  );

  const dynamicConfig = createDynamicConfig(
    queryResult.data as
      | { tableArributes?: Record<string, { name: string }[]> }
      | undefined
  );

  return (
    <>
      <AttributeMapping
        config={dynamicConfig}
        initialMappings={currentMappings}
        onMappingsChange={(newMappings) => {
          handleMappingsChange(entitlementIndex, newMappings);
        }}
        showSaveButton={false}
      />

      {validationError && <ErrorBanner message={validationError} />}
    </>
  );
}

export default RemoveAccessAttributeMappingExpandedView;
